import React, { useEffect, useMemo, useState } from 'react';
import {
    Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement,
    Tooltip, Legend, Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import {
    FaSync, FaUsers, FaChartLine, FaComments, FaBullhorn, FaFire,
    FaCheckCircle, FaExclamationTriangle, FaLightbulb, FaGlobe, FaExchangeAlt, FaTimes,
} from 'react-icons/fa';
import { socialApi } from './api';
import {
    KIND, PLAT, NAVY, ORANGE, RANGES, fmt, round1, nssColor, movingAvg,
    dateFromDays, addDaysStr, acctHandle, kindOf, TopPostCard, PostModal,
} from './analyticsShared';
import './generalDashboard.css';
import './comparator.css';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend, Filler);

const SIDE = { a: { color: NAVY, label: 'A' }, b: { color: ORANGE, label: 'B' } };

// Métricas de una cuenta a partir de su overview.
function metricsOf(account, ov) {
    const rg = ov?.reach_growth || {};
    const eng = ov?.engagement || {};
    const sent = ov?.sentiment || {};
    return {
        followers: account?.followers ?? rg.followers ?? null,
        er: eng.avg_engagement_rate != null ? round1(eng.avg_engagement_rate) : null,
        nss: sent.analyzed ? sent.nss : null,
        posts: eng.posts || 0,
        growth: rg.followers_growth || 0,
        flow: ov?.sentiment_flow || [],
        analyzed: sent.analyzed || 0,
    };
}

function Picker({ accounts, value, onChange, color }) {
    return (
        <div className="cmp-picker" style={{ borderColor: color }}>
            <select value={value || ''} onChange={(e) => onChange(Number(e.target.value))}>
                <option value="" disabled>Elige una cuenta…</option>
                {accounts.map((a) => (
                    <option key={a.id} value={a.id}>{acctHandle(a)} · {KIND[kindOf(a)].label} · {PLAT[a.platform]?.label || a.platform}</option>
                ))}
            </select>
        </div>
    );
}

// Modal: publicaciones publicadas en la fecha/semana clickeada, dividido por cuenta.
function DayPostsModal({ date, bucket, groups, onOpenPost, onClose }) {
    const end = addDaysStr(date, bucket === 'day' ? 1 : 7);
    const inWin = (arr) => (arr || [])
        .filter((p) => { const d = (p.posted_at || '').slice(0, 10); return d >= date && d < end; })
        .sort((a, b) => (b.er ?? -1) - (a.er ?? -1));
    const secs = groups.map((g) => ({ ...g, list: inWin(g.posts) }));
    const empty = secs.every((s) => s.list.length === 0);
    return (
        <div className="sa-modal-overlay" onClick={onClose}>
            <div className="gd-acct-modal" onClick={(e) => e.stopPropagation()}>
                <button className="sa-modal-close" onClick={onClose}><FaTimes /></button>
                <div className="gd-acct-head">
                    <span className="gd-acct-ic" style={{ color: ORANGE }}><FaFire /></span>
                    <div className="gd-acct-id">
                        <h3>Publicaciones del {date}{bucket === 'week' ? ` al ${addDaysStr(date, 6)}` : ''}</h3>
                    </div>
                </div>
                {empty ? (
                    <p className="gd-note">No se publicaron posts en esta fecha en ninguna de las dos cuentas. La línea de NSS refleja el sentimiento de los <b>comentarios recibidos</b> ese día, que pueden ser sobre publicaciones anteriores.</p>
                ) : secs.map((s, i) => s.list.length === 0 ? null : (
                    <div className="gd-day-group" key={i}>
                        <h5 style={{ color: s.color }}>{s.label} · {s.list.length}</h5>
                        <div className="gd-top-grid">
                            {s.list.map((p) => <TopPostCard key={p.id} post={p} onOpen={onOpenPost} />)}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

export default function AccountComparator({ username }) {
    const [accounts, setAccounts] = useState([]);
    const [rangeDays, setRangeDays] = useState(90);
    const [flowBucket, setFlowBucket] = useState('day');   // 'day' (default) | 'week'
    const [smooth, setSmooth] = useState(true);
    const [flowDay, setFlowDay] = useState(null);          // fecha clickeada en la gráfica
    const [idA, setIdA] = useState(null);
    const [idB, setIdB] = useState(null);
    const [dataA, setDataA] = useState({ ov: null, posts: [] });
    const [dataB, setDataB] = useState({ ov: null, posts: [] });
    const [loadingA, setLoadingA] = useState(false);
    const [loadingB, setLoadingB] = useState(false);
    const [modalPost, setModalPost] = useState(null);
    const [err, setErr] = useState('');

    // Cuentas de la org.
    useEffect(() => {
        if (!username) return undefined;
        let cancelled = false;
        setErr('');
        socialApi.listAccounts(username)
            .then((r) => {
                if (cancelled) return;
                const accs = r.accounts || [];
                setAccounts(accs);
                // Defaults: primera propia vs primera de interés (o las dos primeras).
                const own = accs.find((a) => kindOf(a) === 'own');
                const interest = accs.find((a) => kindOf(a) === 'interest');
                setIdA((own || accs[0])?.id ?? null);
                setIdB((interest || accs.find((a) => a.id !== (own || accs[0])?.id))?.id ?? null);
            })
            .catch((e) => { if (!cancelled) setErr(e.message || 'No se pudo cargar la información'); });
        return () => { cancelled = true; };
    }, [username]);

    // Fetch por lado (overview + posts). Etiqueta los posts para el modal/miniaturas.
    const fetchSide = (id, setData, setLoading) => {
        if (!username || !id) { setData({ ov: null, posts: [] }); return undefined; }
        let cancelled = false;
        const acc = accounts.find((a) => a.id === id);
        const from = dateFromDays(rangeDays);
        setLoading(true);
        Promise.allSettled([
            socialApi.overview(username, id, from, '', flowBucket),
            socialApi.posts(username, id, from, ''),
        ]).then(([ov, ps]) => {
            if (cancelled) return;
            const posts = ps.status === 'fulfilled' ? (ps.value.posts || []) : [];
            setData({
                ov: ov.status === 'fulfilled' ? ov.value.overview : null,
                posts: posts.map((p) => ({ ...p, _platform: acc?.platform, _handle: acc?.username, _kind: kindOf(acc), _track_by: acc?.track_by })),
            });
        }).finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => fetchSide(idA, setDataA, setLoadingA), [idA, rangeDays, flowBucket, accounts]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => fetchSide(idB, setDataB, setLoadingB), [idB, rangeDays, flowBucket, accounts]);

    const accA = accounts.find((a) => a.id === idA) || null;
    const accB = accounts.find((a) => a.id === idB) || null;
    const mA = useMemo(() => metricsOf(accA, dataA.ov), [accA, dataA.ov]);
    const mB = useMemo(() => metricsOf(accB, dataB.ov), [accB, dataB.ov]);
    const loading = loadingA || loadingB;

    // Filas de métricas con ganador (mayor = mejor en todas).
    const rows = [
        { key: 'followers', label: 'Seguidores', icon: <FaUsers />, a: mA.followers, b: mB.followers, kind: 'num' },
        { key: 'er', label: 'Engagement rate', icon: <FaChartLine />, a: mA.er, b: mB.er, kind: 'pct' },
        { key: 'nss', label: 'NSS (sentimiento)', icon: <FaComments />, a: mA.nss, b: mB.nss, kind: 'nss' },
        { key: 'posts', label: 'Publicaciones', icon: <FaBullhorn />, a: mA.posts, b: mB.posts, kind: 'num' },
        { key: 'growth', label: 'Crecimiento', icon: <FaFire />, a: mA.growth, b: mB.growth, kind: 'signed' },
    ];
    const disp = (v, kind) => (v == null ? '—' : kind === 'pct' ? `${v}%` : kind === 'signed' ? (v ? `${v > 0 ? '▲' : '▼'} ${fmt(Math.abs(v))}` : '—') : kind === 'nss' ? String(v) : fmt(v));
    const winnerOf = (r) => {
        if (r.a == null || r.b == null || r.a === r.b) return null;
        return r.a > r.b ? 'a' : 'b';
    };

    // Gráfica NSS superpuesta (suavizada).
    const buckets = Array.from(new Set([
        ...mA.flow.map((f) => f.bucket), ...mB.flow.map((f) => f.bucket),
    ])).sort();
    const flowVals = (flow) => {
        const m = Object.fromEntries(flow.map((f) => [f.bucket, f.nss]));
        const raw = buckets.map((b) => (b in m ? m[b] : null));
        return smooth ? movingAvg(raw, 3) : raw;
    };
    const hasFlow = mA.analyzed > 0 || mB.analyzed > 0;

    const bestA = [...dataA.posts].sort((x, y) => (y.er ?? -1) - (x.er ?? -1)).slice(0, 4);
    const bestB = [...dataB.posts].sort((x, y) => (y.er ?? -1) - (x.er ?? -1)).slice(0, 4);

    // Veredicto derivado.
    const insights = [];
    if (accA && accB) {
        if (mA.followers != null && mB.followers != null) {
            const win = mA.followers >= mB.followers ? accA : accB;
            const d = Math.abs(mA.followers - mB.followers);
            insights.push({ color: '#0ea5e9', icon: <FaUsers />, text: `${acctHandle(win)} lidera en seguidores por ${fmt(d)}.` });
        }
        if (mA.er != null && mB.er != null) {
            const win = mA.er >= mB.er ? { acc: accA, m: mA } : { acc: accB, m: mB };
            const lose = win.acc === accA ? { acc: accB, m: mB } : { acc: accA, m: mA };
            const underdog = win.m.followers != null && lose.m.followers != null && win.m.followers < lose.m.followers;
            insights.push({
                color: underdog ? '#d97706' : '#16a34a',
                icon: underdog ? <FaExclamationTriangle /> : <FaCheckCircle />,
                text: underdog
                    ? `${acctHandle(win.acc)} gana en engagement (${win.m.er}%) PESE a tener menos seguidores → su contenido convierte mejor. Estudia su formato.`
                    : `${acctHandle(win.acc)} tiene el mejor engagement rate (${win.m.er}%).`,
            });
        }
        if (mA.nss != null && mB.nss != null) {
            const win = mA.nss >= mB.nss ? accA : accB;
            insights.push({ color: nssColor(Math.max(mA.nss, mB.nss)), icon: <FaComments />, text: `${acctHandle(win)} tiene mejor sentimiento (NSS ${Math.max(mA.nss, mB.nss)}).` });
        } else {
            insights.push({ color: '#94a3b8', icon: <FaExclamationTriangle />, text: 'Sentimiento sin comparar: analiza comentarios de ambas cuentas para activar el NSS.' });
        }
    }

    const swap = () => { setIdA(idB); setIdB(idA); };

    return (
        <div className="gd-root">
            <div className="gd-head">
                <div>
                    <h2 className="gd-title"><FaExchangeAlt /> Comparador de cuentas</h2>
                    <p className="gd-subtitle">Enfrenta dos cuentas (propias o de interés) métrica por métrica.</p>
                </div>
                <div className="gd-head-actions">
                    <select value={rangeDays} onChange={(e) => setRangeDays(Number(e.target.value))} style={{ cursor: 'pointer' }}>
                        {RANGES.map((r) => <option key={r.days} value={r.days}>{r.label}</option>)}
                    </select>
                </div>
            </div>

            {err && <div className="gd-card" style={{ borderColor: '#fecaca', background: '#fef2f2', color: '#b91c1c', marginBottom: 16 }}>{err}</div>}
            {!username && <div className="gd-card">Inicia sesión para usar el comparador.</div>}

            {username && accounts.length < 2 && (
                <div className="gd-card">
                    <h4>Necesitas al menos 2 cuentas</h4>
                    <p className="gd-subtitle" style={{ margin: 0 }}>Agrega cuentas en la pestaña <b>Redes Sociales</b> para poder compararlas.</p>
                </div>
            )}

            {username && accounts.length >= 2 && (
                <>
                    {/* Selectores */}
                    <div className="cmp-pickers">
                        <Picker accounts={accounts} value={idA} onChange={setIdA} color={NAVY} />
                        <button className="cmp-swap" onClick={swap} title="Intercambiar"><FaExchangeAlt /></button>
                        <Picker accounts={accounts} value={idB} onChange={setIdB} color={ORANGE} />
                    </div>

                    {loading && <div className="gd-streaming"><FaSync className="spin" /> Cargando métricas…</div>}

                    {/* Encabezados de cuenta */}
                    <div className="cmp-heads">
                        {[{ acc: accA, side: 'a' }, { acc: accB, side: 'b' }].map(({ acc, side }) => {
                            const meta = PLAT[acc?.platform] || {};
                            const Icon = meta.Icon || FaGlobe;
                            return (
                                <div className="cmp-head-card" key={side} style={{ borderTopColor: SIDE[side].color }}>
                                    <span className="cmp-head-ic" style={{ color: meta.color }}><Icon /></span>
                                    <span className="cmp-head-handle">{acc ? acctHandle(acc) : '—'}</span>
                                    {acc && <span className="gd-kind-pill" style={{ color: KIND[kindOf(acc)].color, borderColor: KIND[kindOf(acc)].color }}>{KIND[kindOf(acc)].label}</span>}
                                </div>
                            );
                        })}
                    </div>

                    {/* Métricas enfrentadas */}
                    <div className="gd-card cmp-metrics">
                        {rows.map((r) => {
                            const w = winnerOf(r);
                            return (
                                <div className="cmp-row" key={r.key}>
                                    <span className={`cmp-val ${w === 'a' ? 'win' : ''}`}>{disp(r.a, r.kind)}{w === 'a' && <em>▲</em>}</span>
                                    <span className="cmp-mlabel">{r.icon} {r.label}</span>
                                    <span className={`cmp-val cmp-right ${w === 'b' ? 'win' : ''}`}>{w === 'b' && <em>▲</em>}{disp(r.b, r.kind)}</span>
                                </div>
                            );
                        })}
                    </div>

                    {/* NSS superpuesto */}
                    <div className="gd-card">
                        <div className="gd-card-head">
                            <h4>Sentimiento en el tiempo (NSS)</h4>
                            <div className="gd-flow-controls">
                                <div className="gd-seg">
                                    <button className={flowBucket === 'week' ? 'active' : ''} onClick={() => setFlowBucket('week')}>Semana</button>
                                    <button className={flowBucket === 'day' ? 'active' : ''} onClick={() => setFlowBucket('day')}>Día</button>
                                </div>
                                <button className={`gd-smooth ${smooth ? 'active' : ''}`} onClick={() => setSmooth((s) => !s)}>
                                    <FaChartLine /> Suavizar
                                </button>
                            </div>
                        </div>
                        <div className="gd-chart">
                            {!hasFlow ? (
                                <div className="gd-empty-guard"><FaComments /><p>Sin comentarios analizados en ninguna de las dos cuentas.</p></div>
                            ) : (
                                <Line
                                    data={{
                                        labels: buckets.map((b) => (b || '').slice(0, 10)),
                                        datasets: [
                                            { label: accA ? acctHandle(accA) : 'A', data: flowVals(mA.flow), borderColor: NAVY, backgroundColor: 'rgba(7,60,92,.10)', fill: true, tension: smooth ? 0.45 : 0.3, pointRadius: 2, borderWidth: 2, spanGaps: true },
                                            { label: accB ? acctHandle(accB) : 'B', data: flowVals(mB.flow), borderColor: ORANGE, backgroundColor: 'transparent', fill: false, tension: smooth ? 0.45 : 0.3, pointRadius: 2, borderWidth: 2, borderDash: [5, 3], spanGaps: true },
                                        ],
                                    }}
                                    options={{
                                        responsive: true, maintainAspectRatio: false,
                                        interaction: { mode: 'index', intersect: false },
                                        onClick: (evt, els) => { if (els.length) setFlowDay((buckets[els[0].index] || '').slice(0, 10)); },
                                        onHover: (evt, els) => { const t = evt?.native?.target; if (t) t.style.cursor = els.length ? 'pointer' : 'default'; },
                                        plugins: { legend: { position: 'bottom', labels: { usePointStyle: true, boxWidth: 8 } } },
                                        scales: { y: { min: -100, max: 100, grid: { color: '#f1f5f9' } }, x: { grid: { display: false } } },
                                    }}
                                />
                            )}
                        </div>
                        {hasFlow && <p className="gd-note">Haz click en un punto para ver las publicaciones de esa fecha.</p>}
                    </div>

                    {/* Veredicto */}
                    {insights.length > 0 && (
                        <div className="gd-card gd-ai">
                            <div className="gd-ai-head"><span className="gd-ai-badge"><FaLightbulb /> Veredicto</span></div>
                            {insights.map((a, i) => (
                                <div className="gd-ai-row" key={i}><span style={{ color: a.color }}>{a.icon}</span><p>{a.text}</p></div>
                            ))}
                        </div>
                    )}

                    {/* Mejores publicaciones lado a lado */}
                    <div className="gd-card">
                        <h4><FaFire style={{ color: ORANGE }} /> Mejores publicaciones</h4>
                        <div className="cmp-posts">
                            {[{ acc: accA, best: bestA, loading: loadingA }, { acc: accB, best: bestB, loading: loadingB }].map(({ acc, best, loading: l }, i) => (
                                <div className="cmp-posts-col" key={i}>
                                    <h5>{acc ? acctHandle(acc) : '—'}</h5>
                                    {best.length === 0 ? (
                                        <p className="gd-subtitle">{l ? 'Cargando…' : 'Sin publicaciones en el rango.'}</p>
                                    ) : (
                                        <div className="gd-top-grid cmp-top-grid">
                                            {best.map((p) => <TopPostCard key={p.id} post={p} onOpen={setModalPost} />)}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </>
            )}

            {flowDay && (
                <DayPostsModal
                    date={flowDay} bucket={flowBucket}
                    groups={[
                        { label: accA ? acctHandle(accA) : 'Cuenta A', color: NAVY, posts: dataA.posts },
                        { label: accB ? acctHandle(accB) : 'Cuenta B', color: ORANGE, posts: dataB.posts },
                    ]}
                    onOpenPost={(p) => { setFlowDay(null); setModalPost(p); }}
                    onClose={() => setFlowDay(null)}
                />
            )}
            {modalPost && <PostModal username={username} post={modalPost} onClose={() => setModalPost(null)} />}
        </div>
    );
}
